import { computed, effect, inject, Injectable, NgZone, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { OptionType } from '../enums/OptionType';
import {
  OnlineOrder,
  OnlineOrderSource,
  OnlineOrderStatus,
  OnlineOrderType,
} from '../models/OnlineOrder.model';
import { checkSetOrderItem, OrderItem } from '../models/OrderItem.model';
import Product from '../models/Product.model';
import ProductOption from '../models/ProductOption.model';
import { AppSettingsService } from './app-settings.service';
import { GeoService } from './geo.service';
import { PBService } from './pocketbase.service';

const CART_STORAGE_KEY = 'bistronex_qr_cart_items';
const MY_ORDERS_STORAGE_KEY = 'bistronex_qr_my_order_ids';
const ACTIVE_ORDERS_STORAGE_KEY = 'bistronex_qr_active_order_ids';
const LEGACY_ACTIVE_ORDER_STORAGE_KEY = 'bistronex_qr_active_order_id';
const PATRON_ID_STORAGE_KEY = 'bistronex_qr_patron_id';
const MAX_ORDER_AGE_MS = 12 * 60 * 60 * 1000; // 12 hours

export interface LastAddedFeedback {
  productId: string;
  name: any;
  quantity: number;
  timestamp: number;
}

/**
 * Ensures a PocketBase timestamp is strictly parsed as UTC0 epoch milliseconds.
 * PocketBase stores created/updated timestamps in UTC0 (e.g. "2026-09-19 15:30:54.766Z").
 */
export function parseUtcEpochMs(dateStr?: string | null): number {
  if (!dateStr) return Date.now();
  let s = dateStr.trim();
  if (!s.includes('T')) s = s.replace(' ', 'T');
  if (!s.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(s)) s += 'Z';
  const ms = new Date(s).getTime();
  return isNaN(ms) ? Date.now() : ms;
}

@Injectable({ providedIn: 'root' })
export class CartService {
  private settings = inject(AppSettingsService);
  private pb = inject(PBService);
  public geoService = inject(GeoService);
  private translate = inject(TranslateService);
  private ngZone = inject(NgZone);

  public orderItems = signal<OrderItem[]>([]);
  public orderType = signal<OnlineOrderType>(OnlineOrderType.DINE_IN);
  public orderNotes = signal<string>('');

  // Cart animation feedback
  public lastAddedItem = signal<LastAddedFeedback | null>(null);
  private lastAddedTimeout: any = null;

  // Multi-order tracking signals
  public activeOrders = signal<OnlineOrder[]>([]);
  public selectedTrackingOrderId = signal<string | null>(null);

  // Table switch prompt state
  public pendingTableSwitch = signal<{ currentTableID: string; newTableID: string; newTableName?: string } | null>(null);
  private currentAcknowledgedTable: string | null = null;

  // Computeds
  public activeOrder = computed(() => {
    const selectedId = this.selectedTrackingOrderId();
    const orders = this.activeOrders();
    if (selectedId) {
      const found = orders.find(o => o.id === selectedId);
      if (found) return found;
    }
    return orders.length > 0 ? orders[0] : null;
  });

  public currencyPrecision = computed(() => {
    const p = this.settings.selectedCurrency()?.Precision;
    return typeof p === 'number' && !isNaN(p) ? p : 2;
  });

  public currencySymbol = computed(() => {
    return this.settings.selectedCurrency()?.Symbol || '$';
  });

  public companyCurrency = computed(() => {
    return this.currencySymbol();
  });

  public totalAllActiveOrders = computed(() => {
    return Number(this.activeOrders().reduce((sum, o) => sum + (o.PayableAmount || 0), 0).toFixed(this.currencyPrecision()));
  });

  public isCartOpen = signal<boolean>(false);
  public isTrackingOpen = signal<boolean>(false);
  public isSubmitting = signal<boolean>(false);
  public submitError = signal<string | null>(null);

  private activeSubscriptionOrderId: string | null = null;

  public itemCount = computed(() =>
    this.orderItems().reduce((sum, item) => sum + item.Quantity, 0)
  );

  public grossTotal = computed(() =>
    Number(this.orderItems().reduce((sum, item) => sum + item.OrderItemGrossAmount, 0).toFixed(this.currencyPrecision()))
  );

  public taxTotal = computed(() =>
    Number(this.orderItems().reduce((sum, item) => sum + (item.OrderItemTaxAmount || 0), 0).toFixed(this.currencyPrecision()))
  );

  public payableTotal = computed(() =>
    Number(this.orderItems().reduce((sum, item) => sum + item.OrderItemTotalPayable, 0).toFixed(this.currencyPrecision()))
  );

  // Tipping (Presets: 0, 5, 10, 15, or 'custom')
  public selectedTipPreset = signal<number | 'custom'>(0);
  public customTipAmount = signal<number>(0);

  // Tip calculation based on food subtotal (grossTotal), non-taxable
  public tipAmount = computed(() => {
    const preset = this.selectedTipPreset();
    const precision = this.currencyPrecision();
    if (typeof preset === 'number') {
      if (preset <= 0) return 0;
      return Number(((this.grossTotal() * preset) / 100).toFixed(precision));
    }
    const custom = this.customTipAmount();
    return Number(Math.max(0, custom || 0).toFixed(precision));
  });

  public deliveryFee = signal<number>(0);
  public customerEmail = signal<string>('');

  // Total payable including non-taxable tip & delivery fee
  public finalPayableTotal = computed(() => {
    const precision = this.currencyPrecision();
    return Number((this.payableTotal() + this.tipAmount() + this.deliveryFee()).toFixed(precision));
  });

  public setTipPreset(preset: number | 'custom') {
    this.selectedTipPreset.set(preset);
  }

  public setCustomTip(amount: number) {
    this.customTipAmount.set(Math.max(0, amount || 0));
    this.selectedTipPreset.set('custom');
  }

  public setCustomerEmail(email: string) {
    this.customerEmail.set(email);
  }

  public canSwitchOrderType = computed(() =>
    this.settings.tableOrderEnabled() &&
    this.settings.pickupOrderEnabled() &&
    !!this.settings.tableID()
  );

  // Order Limits & Gating (compared against order total without tip)
  public minOrderTotal = computed(() => this.settings.minOrderTotal());
  public maxOrderTotal = computed(() => this.settings.maxOrderTotal());

  public orderTotalWithoutTip = computed(() => {
    const precision = this.currencyPrecision();
    return Number((this.payableTotal() + this.deliveryFee()).toFixed(precision));
  });

  public isBelowMinOrder = computed(() => {
    const min = this.minOrderTotal();
    return min !== null && min > 0 && this.orderTotalWithoutTip() < min;
  });

  public minOrderRemaining = computed(() => {
    const min = this.minOrderTotal();
    return this.isBelowMinOrder() && min !== null ? Number((min - this.orderTotalWithoutTip()).toFixed(this.currencyPrecision())) : 0;
  });

  public isAboveMaxOrder = computed(() => {
    const max = this.maxOrderTotal();
    return max !== null && max > 0 && this.orderTotalWithoutTip() > max;
  });

  // Operating Hours Guard
  public isRestaurantOpen = computed(() => this.settings.isRestaurantOpen());
  public operatingStatus = computed(() => this.settings.operatingStatus());

  // Payment Gating (0: Pay at Counter / Cash, 1: Online Payment)
  public selectedPaymentType = signal<number>(0);

  public isPaymentRequired = computed(() => {
    const type = this.orderType();
    const qr = this.settings.qrMenuSettings();
    if (qr.PaymentRequired === true) {
      return true;
    }
    if (type === OnlineOrderType.DINE_IN) {
      return qr.TableOrder.PaymentRequired === true;
    }
    if (type === OnlineOrderType.PICKUP) {
      return qr.PickupOrder.PaymentRequired === true;
    }
    return false;
  });

  public hasUnsellableItems = computed(() =>
    this.orderItems().some(item => item.product?.IsSellable === false)
  );

  public canSubmitOrder = computed(() => {
    if (this.orderItems().length === 0) return false;
    if (this.isSubmitting()) return false;
    if (!this.settings.isOrderingEnabled()) return false;
    if (!this.isRestaurantOpen()) return false;
    if (this.isBelowMinOrder()) return false;
    if (this.isAboveMaxOrder()) return false;
    if (this.hasUnsellableItems()) return false;
    if (this.orderType() === OnlineOrderType.DINE_IN && !this.settings.tableID()) return false;
    return true;
  });

  constructor() {
    this.restoreFromStorage();

    // Check if user returned from a cancelled Stripe payment session
    if (typeof window !== 'undefined' && typeof window.location !== 'undefined') {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('payment_cancelled') === 'true') {
          this.isCartOpen.set(true);
          const cancelMsg = this.translate.instant('CART.PAYMENT_CANCELLED') || 'Payment was cancelled. Your items are still in your cart.';
          this.submitError.set(cancelMsg);

          // Clean up the URL parameter without page reload
          urlParams.delete('payment_cancelled');
          const newSearch = urlParams.toString();
          const cleanUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
          window.history.replaceState({}, '', cleanUrl);
        }
      } catch (_) { }
    }

    // Automatically synchronize payment type when required
    effect(() => {
      if (this.isPaymentRequired()) {
        this.selectedPaymentType.set(1);
      }
    });

    // Automatically synchronize order type when tableID or AppSettings change
    effect(() => {
      const table = this.settings.tableID();
      const tableEnabled = this.settings.tableOrderEnabled();
      const pickupEnabled = this.settings.pickupOrderEnabled();

      if (!this.settings.isOrderingEnabled()) {
        return;
      }

      if (tableEnabled && !pickupEnabled) {
        if (this.orderType() !== OnlineOrderType.DINE_IN) {
          this.setOrderType(OnlineOrderType.DINE_IN);
        }
      } else if (!tableEnabled && pickupEnabled) {
        if (this.orderType() !== OnlineOrderType.PICKUP) {
          this.setOrderType(OnlineOrderType.PICKUP);
        }
      } else if (tableEnabled && pickupEnabled) {
        if (!table && this.orderType() === OnlineOrderType.DINE_IN) {
          this.setOrderType(OnlineOrderType.PICKUP);
        } else if (table && this.orderType() === OnlineOrderType.PICKUP && this.orderItems().length === 0) {
          this.setOrderType(OnlineOrderType.DINE_IN);
        }
      }
    });

    // Detect if patron scanned a different table while having active orders on another table
    effect(() => {
      const newTable = this.settings.tableID();
      const orders = this.activeOrders();
      if (newTable && orders.length > 0) {
        // Check if existing orders belong to a different table
        const firstOrderWithTable = orders.find(o => o.TableID && o.TableID.length > 0);
        if (firstOrderWithTable && firstOrderWithTable.TableID !== newTable) {
          if (this.currentAcknowledgedTable !== newTable) {
            this.pendingTableSwitch.set({
              currentTableID: firstOrderWithTable.TableID!,
              newTableID: newTable,
              newTableName: this.settings.currentTable()?.Name || newTable,
            });
          }
        } else {
          this.currentAcknowledgedTable = newTable;
        }
      } else if (newTable) {
        this.currentAcknowledgedTable = newTable;
      }
    });

    // Save cart items to local storage on changes
    effect(() => {
      const items = this.orderItems();
      try {
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
      } catch (e) {
        console.error('Failed to save cart to localStorage', e);
      }
    });

    // Load/refresh this patron's orders whenever customer is set
    effect(() => {
      const cust = this.settings.customer;
      if (cust) {
        this.loadAllActiveOrders();
      }
    });

    // Refresh active orders when tab becomes visible after device lock/backgrounding
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && this.activeOrders().length > 0) {
          this.loadAllActiveOrders();
        }
      });
    }
  }

  public getExistingPatronId(): string | null {
    try {
      return localStorage.getItem(PATRON_ID_STORAGE_KEY) || null;
    } catch (_) {
      return null;
    }
  }

  public getOrCreatePatronId(): string {
    try {
      let patronId = localStorage.getItem(PATRON_ID_STORAGE_KEY);
      if (!patronId || patronId.trim().length === 0) {
        patronId = typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : 'patron_' + Math.random().toString(36).substring(2) + Date.now();
        localStorage.setItem(PATRON_ID_STORAGE_KEY, patronId);
      }
      return patronId;
    } catch (_) {
      return 'patron_' + Date.now();
    }
  }

  private restoreFromStorage() {
    try {
      const savedItems = localStorage.getItem(CART_STORAGE_KEY);
      if (savedItems) {
        const parsed: OrderItem[] = JSON.parse(savedItems);
        if (Array.isArray(parsed)) {
          this.orderItems.set(parsed.map(item => checkSetOrderItem(item)));
        }
      }

      this.loadAllActiveOrders();
    } catch (e) {
      console.error('Error restoring cart from storage', e);
    }
  }

  /**
   * Loads ONLY this patron's own orders (strictly isolated to this browser/device).
   * If this device has no PatronID and no stored order IDs, it does not query or subscribe.
   * Automatically cleans up orders older than 12 hours or orders that are completed.
   */
  public async loadAllActiveOrders() {
    try {
      const storedIds: string[] = [];
      const savedJson = localStorage.getItem(MY_ORDERS_STORAGE_KEY);
      if (savedJson) {
        try {
          const parsed = JSON.parse(savedJson);
          if (Array.isArray(parsed)) storedIds.push(...parsed);
        } catch (_) { }
      }

      // Check legacy keys for migration
      const legacyArr = localStorage.getItem(ACTIVE_ORDERS_STORAGE_KEY);
      if (legacyArr) {
        try {
          const parsed = JSON.parse(legacyArr);
          if (Array.isArray(parsed)) {
            for (const id of parsed) {
              if (!storedIds.includes(id)) storedIds.push(id);
            }
          }
        } catch (_) { }
      }
      const legacyId = localStorage.getItem(LEGACY_ACTIVE_ORDER_STORAGE_KEY);
      if (legacyId && !storedIds.includes(legacyId)) {
        storedIds.push(legacyId);
      }

      const patronId = this.getExistingPatronId();

      // If no PatronID exists in storage AND no stored order IDs, this browser
      // has never placed an order: DO NOT query, DO NOT subscribe!
      if (!patronId && storedIds.length === 0) {
        this.activeOrders.set([]);
        this.stopAllSubscriptions();
        return;
      }

      const orderMap = new Map<string, OnlineOrder>();
      const now = Date.now();
      const currentCustId = this.settings.customer?.id;

      // Consolidate stored order IDs and PatronID into a single batch query
      const idFilters: string[] = [];
      for (const id of storedIds) {
        if (id && id.trim()) {
          idFilters.push(`id = "${id.trim()}"`);
        }
      }

      const orClauses: string[] = [];
      if (idFilters.length > 0) {
        orClauses.push(`(${idFilters.join(' || ')})`);
      }
      if (patronId) {
        orClauses.push(`PatronID = "${patronId}"`);
      }

      if (orClauses.length > 0) {
        // PocketBase stores timestamps in UTC0 ("YYYY-MM-DD HH:mm:ss")
        const twelveHoursAgoUtc = new Date(now - MAX_ORDER_AGE_MS).toISOString().replace('T', ' ').slice(0, 19);
        let filterStr = `(${orClauses.join(' || ')}) && Status != 99 && created >= "${twelveHoursAgoUtc}"`;
        if (currentCustId) {
          filterStr = `(${filterStr}) && CustomerID = "${currentCustId}"`;
        }

        try {
          const res = await this.pb.getOnlineOrders({
            filter: filterStr,
            sort: 'created',
            perPage: 50,
          });
          for (const ord of res.items) {
            const createdMs = parseUtcEpochMs(ord.created);
            const isFresh = (now - createdMs) < MAX_ORDER_AGE_MS;
            if (isFresh && ord.Status !== OnlineOrderStatus.CANCELLED) {
              orderMap.set(ord.id!, ord);
            }
          }
        } catch (e) {
          console.warn('Could not batch-load patron orders:', e);
        }
      }

      const orderList = Array.from(orderMap.values());
      // Sort chronologically (oldest first) so #1 is the first order of the session
      orderList.sort((a, b) => parseUtcEpochMs(a.created) - parseUtcEpochMs(b.created));

      this.activeOrders.set(orderList);

      // Save valid active order IDs to storage
      const activeIds = orderList.map(o => o.id!);
      try {
        localStorage.setItem(MY_ORDERS_STORAGE_KEY, JSON.stringify(activeIds));
      } catch (_) { }

      if (orderList.length > 0) {
        if (!this.selectedTrackingOrderId() || !orderMap.has(this.selectedTrackingOrderId()!)) {
          // Default to the newest order
          this.selectedTrackingOrderId.set(orderList[orderList.length - 1].id!);
        }
        // Subscribe to each active order's specific record topic (OnlineOrder/:id)
        for (const ord of orderList) {
          if (ord.id) {
            this.subscribeToOrder(ord.id);
          }
        }
        if (patronId) {
          this.subscribeToPatron(patronId);
        }
        this.startBackupPolling();
      } else {
        this.stopAllSubscriptions();
        this.stopBackupPolling();
      }
    } catch (e) {
      console.error('Error loading patron orders:', e);
    }
  }

  /**
   * Sets order type:
   * Dine-in uses Price1 / Tax1.
   * Pickup uses Price3 / Tax3 (falling back to Price1 / Tax1).
   */
  public setOrderType(newType: OnlineOrderType) {
    if (newType === OnlineOrderType.DINE_IN && !this.settings.tableOrderEnabled()) {
      return;
    }
    if (newType === OnlineOrderType.PICKUP && !this.settings.pickupOrderEnabled()) {
      return;
    }

    this.orderType.set(newType);

    // Recalculate all items base prices & taxes based on new order type
    this.orderItems.update(items => {
      return items.map(item => {
        if (item.product) {
          const { basePrice, taxPercent } = this.resolveProductPricing(item.product, newType);
          let multiplier = 1.0;
          for (const opt of (item.OrderItemFeature || [])) {
            if (opt.Type === OptionType.MULTIPLY && opt.Modifier > 0) {
              multiplier = opt.Modifier;
              break;
            }
          }
          item.UnitBasePrice = Number((basePrice * multiplier).toFixed(4));
          item.OrderItemTaxPercentage = taxPercent;
        }
        item.TableID = newType === OnlineOrderType.DINE_IN ? (this.settings.tableID() || null) : null;
        item.FloorID = newType === OnlineOrderType.DINE_IN ? (this.settings.floorID() || null) : null;
        return checkSetOrderItem(item);
      });
    });
  }

  private resolveProductPricing(product: Product, type: OnlineOrderType): { basePrice: number; taxPercent: number } {
    let basePrice: number;
    let taxPercent: number;

    if (type === OnlineOrderType.PICKUP) {
      // Pickup uses Price3 / Tax3 (Register tier)
      basePrice = (typeof product.Price3 === 'number' && product.Price3 > 0)
        ? product.Price3
        : (typeof product.Price1 === 'number' ? product.Price1 : 0);

      taxPercent = (typeof product.Tax3 === 'number' && product.Tax3 > 0)
        ? product.Tax3
        : (typeof product.Tax1 === 'number' ? product.Tax1 : 0);
    } else {
      // Dine-in uses Price1 / Tax1 (Table tier)
      basePrice = typeof product.Price1 === 'number' ? product.Price1 : 0;
      taxPercent = typeof product.Tax1 === 'number' ? product.Tax1 : 0;
    }

    return { basePrice, taxPercent };
  }

  public addToCart(product: Product, selectedOptions: ProductOption[], quantity: number = 1, notes: string = '') {
    if (!this.settings.isOrderingEnabled() || product.IsSellable === false) {
      return;
    }

    const currentOrderType = this.orderType();
    const { basePrice, taxPercent } = this.resolveProductPricing(product, currentOrderType);
    const currency = this.settings.selectedCurrency();
    const tableID = currentOrderType === OnlineOrderType.DINE_IN ? (this.settings.tableID() || null) : null;
    const floorID = currentOrderType === OnlineOrderType.DINE_IN ? (this.settings.floorID() || null) : null;

    // 1. Calculate multiplier from MULTIPLY option
    let multiplier = 1.0;
    for (const opt of selectedOptions) {
      if (opt.Type === OptionType.MULTIPLY && opt.Modifier > 0) {
        multiplier = opt.Modifier;
        break;
      }
    }
    const multipliedBasePrice = Number((basePrice * multiplier).toFixed(4));

    // 2. Map options ensuring Quantity, ReferenceProductID, Modifier, and Type are preserved
    const mappedFeatures: ProductOption[] = selectedOptions.map(opt => ({
      ...opt,
      Quantity: typeof opt.Quantity === 'number' && opt.Quantity > 0 ? opt.Quantity : 1,
      ReferenceProductID: opt.ReferenceProductID || '',
      Modifier: typeof opt.Modifier === 'number' ? opt.Modifier : 0,
      Type: typeof opt.Type === 'number' ? opt.Type : 0,
    }));

    // 3. Deduplicate: check if identical item exists (same product, same options with quantities, same notes)
    const sortedOptionKeys = mappedFeatures
      .map(o => `${o.id}_${o.Quantity || 1}`)
      .sort()
      .join(',');
    const existingIndex = this.orderItems().findIndex(item => {
      if (item.ProductID !== product.id || (item.Description || '') !== notes) {
        return false;
      }
      const itemOptionKeys = (item.OrderItemFeature || [])
        .map(o => `${o.id}_${o.Quantity || 1}`)
        .sort()
        .join(',');
      return itemOptionKeys === sortedOptionKeys;
    });

    if (existingIndex > -1) {
      this.orderItems.update(items => {
        const copy = [...items];
        const existing = copy[existingIndex];
        existing.Quantity += quantity;
        copy[existingIndex] = checkSetOrderItem(existing);
        return copy;
      });
    } else {
      const newItem: OrderItem = {
        id: crypto.randomUUID(),
        VenueID: (this.settings.venue || this.settings.customer)?.id,
        CustomerID: (this.settings.venue || this.settings.customer)?.id,
        ProductID: product.id,
        Name: product.Name,
        SID: product.SID,
        Quantity: quantity,
        ProductType: product.Type,
        OrderType: 0,
        OrderItemFeature: mappedFeatures,
        Description: notes,
        UnitBasePrice: multipliedBasePrice,
        UnitDiscountPrice: 0,
        OrderItemTaxPercentage: taxPercent,
        OrderItemGrossAmount: 0,
        OrderItemTotalPayable: 0,
        DefaultCurrencyID: currency?.id || '',
        TableID: tableID,
        FloorID: floorID,
        product: product,
      };

      const calculatedItem = checkSetOrderItem(newItem);
      this.orderItems.update(prev => [...prev, calculatedItem]);
    }

    // Trigger cart feedback animation
    this.dismissLastAdded();
    this.lastAddedItem.set({
      productId: product.id,
      name: product.Name,
      quantity,
      timestamp: Date.now(),
    });
    this.lastAddedTimeout = setTimeout(() => {
      this.lastAddedItem.set(null);
    }, 2800);
  }

  public dismissLastAdded() {
    if (this.lastAddedTimeout) {
      clearTimeout(this.lastAddedTimeout);
      this.lastAddedTimeout = null;
    }
    this.lastAddedItem.set(null);
  }

  public updateQuantity(itemId: string, delta: number) {
    this.orderItems.update(prev => {
      return prev
        .map(item => {
          if (item.id === itemId) {
            const newQty = item.Quantity + delta;
            if (newQty <= 0) return null;
            item.Quantity = newQty;
            return checkSetOrderItem(item);
          }
          return item;
        })
        .filter((item): item is OrderItem => item !== null);
    });
  }

  public removeItem(itemId: string) {
    this.orderItems.update(prev => prev.filter(i => i.id !== itemId));
  }

  public clearCart() {
    this.orderItems.set([]);
    this.orderNotes.set('');
    this.selectedTipPreset.set(0);
    this.customTipAmount.set(0);
    this.customerEmail.set('');
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
    } catch (e) {
      console.error(e);
    }
  }

  public openCart() {
    if (!this.settings.isOrderingEnabled()) {
      return;
    }
    this.submitError.set(null);
    this.isCartOpen.set(true);
  }

  public closeCart() {
    this.isCartOpen.set(false);
  }

  public openTracking() {
    this.isTrackingOpen.set(true);
  }

  public closeTracking() {
    this.isTrackingOpen.set(false);
  }

  public dismissOrder(orderId: string) {
    this.activeOrders.update(list => list.filter(o => o.id !== orderId));
    const remaining = this.activeOrders();

    const activeIds = remaining.map(o => o.id!);
    try {
      localStorage.setItem(MY_ORDERS_STORAGE_KEY, JSON.stringify(activeIds));
    } catch (_) { }

    if (this.selectedTrackingOrderId() === orderId) {
      this.selectedTrackingOrderId.set(remaining.length > 0 ? remaining[0].id! : null);
    }

    if (remaining.length === 0) {
      this.stopPolling();
      this.isTrackingOpen.set(false);
    }
  }

  public dismissActiveOrder() {
    const currentId = this.selectedTrackingOrderId() || this.activeOrder()?.id;
    if (currentId) {
      this.dismissOrder(currentId);
    } else {
      this.activeOrders.set([]);
      this.isTrackingOpen.set(false);
      this.stopPolling();
    }
  }

  /**
   * Confirms switching to a new table scanned by the patron.
   */
  public confirmTableSwitch() {
    const pending = this.pendingTableSwitch();
    if (pending) {
      this.clearCart();
      this.currentAcknowledgedTable = pending.newTableID;
      this.settings.tableID.set(pending.newTableID);
      this.pendingTableSwitch.set(null);
    }
  }

  /**
   * Cancels switching to a new table, retaining the current table session.
   */
  public cancelTableSwitch() {
    const pending = this.pendingTableSwitch();
    if (pending) {
      this.currentAcknowledgedTable = pending.currentTableID;
      this.settings.tableID.set(pending.currentTableID);
      this.pendingTableSwitch.set(null);
    }
  }

  /**
   * Submits the order via PBService.createOnlineOrder
   */
  public async submitOrder(): Promise<OnlineOrder> {
    const customer = this.settings.customer;
    if (!customer) {
      const errorMsg = this.translate.instant('ERRORS.CUSTOMER_NOT_LOADED') || 'Customer information not loaded.';
      this.submitError.set(errorMsg);
      throw new Error(errorMsg);
    }

    if (!this.settings.isOrderingEnabled()) {
      const errorMsg = this.translate.instant('ERRORS.ORDERING_UNAVAILABLE') || 'Online ordering is currently unavailable.';
      this.submitError.set(errorMsg);
      throw new Error(errorMsg);
    }

    if (!this.settings.isRestaurantOpen()) {
      const status = this.settings.operatingStatus();
      let errorMsg = this.translate.instant('OPERATING_HOURS.CART_CLOSED_MSG') || 'The restaurant is currently closed.';
      if (status.opensAtTime) {
        const timeMsg = status.messageKey === 'OPERATING_HOURS.OPENS_TODAY_AT'
          ? (this.translate.instant('OPERATING_HOURS.OPENS_TODAY_AT', { time: status.opensAtTime }) || `Opens today at ${status.opensAtTime} UTC`)
          : status.messageKey === 'OPERATING_HOURS.OPENS_TOMORROW_AT'
            ? (this.translate.instant('OPERATING_HOURS.OPENS_TOMORROW_AT', { time: status.opensAtTime }) || `Opens tomorrow at ${status.opensAtTime} UTC`)
            : (this.translate.instant('OPERATING_HOURS.OPENS_DAY_AT', { day: status.nextOpenDay, time: status.opensAtTime }) || `Opens ${status.nextOpenDay} at ${status.opensAtTime} UTC`);
        errorMsg += ` (${timeMsg})`;
      }
      this.submitError.set(errorMsg);
      throw new Error(errorMsg);
    }

    if (this.orderItems().length === 0) {
      const errorMsg = this.translate.instant('ERRORS.CART_EMPTY') || 'Cart is empty.';
      this.submitError.set(errorMsg);
      throw new Error(errorMsg);
    }

    if (this.hasUnsellableItems()) {
      const errorMsg = this.translate.instant('ERRORS.UNSELLABLE_ITEMS_IN_CART') || 'Your cart contains unavailable items. Please remove them to proceed.';
      this.submitError.set(errorMsg);
      throw new Error(errorMsg);
    }

    const payable = this.orderTotalWithoutTip();
    const minLimit = this.minOrderTotal();
    if (minLimit !== null && minLimit > 0 && payable < minLimit) {
      const precision = this.currencyPrecision();
      const remaining = Number((minLimit - payable).toFixed(precision));
      const symbol = this.currencySymbol();
      const formattedRemaining = remaining.toFixed(precision);
      const formattedMin = minLimit.toFixed(precision);
      const errorMsg = this.translate.instant('LIMITS.MIN_ORDER_HELP', {
        remaining: `${symbol} ${formattedRemaining}`,
        min: `${symbol} ${formattedMin}`,
      }) || `Minimum order amount is ${symbol} ${formattedMin}. Please add ${symbol} ${formattedRemaining} more to submit your order.`;
      this.submitError.set(errorMsg);
      throw new Error(errorMsg);
    }

    const maxLimit = this.maxOrderTotal();
    if (maxLimit !== null && maxLimit > 0 && payable > maxLimit) {
      const precision = this.currencyPrecision();
      const symbol = this.currencySymbol();
      const formattedMax = maxLimit.toFixed(precision);
      const errorMsg = this.translate.instant('LIMITS.MAX_ORDER_WARN', {
        max: `${symbol} ${formattedMax}`,
      }) || `Order total exceeds the maximum limit of ${symbol} ${formattedMax}.`;
      this.submitError.set(errorMsg);
      throw new Error(errorMsg);
    }

    const type = this.orderType();
    const tableID = this.settings.tableID();

    if (type === OnlineOrderType.DINE_IN && !this.settings.tableOrderEnabled()) {
      const errorMsg = this.translate.instant('ERRORS.TABLE_ORDER_DISABLED') || 'Table ordering is currently unavailable.';
      this.submitError.set(errorMsg);
      throw new Error(errorMsg);
    }

    if (type === OnlineOrderType.PICKUP && !this.settings.pickupOrderEnabled()) {
      const errorMsg = this.translate.instant('ERRORS.PICKUP_ORDER_DISABLED') || 'Pickup ordering is currently unavailable.';
      this.submitError.set(errorMsg);
      throw new Error(errorMsg);
    }

    if (type === OnlineOrderType.DINE_IN && !tableID) {
      const errorMsg = this.translate.instant('ERRORS.TABLE_REQUIRED') || 'Dine-in orders require a table. Please select Pickup or scan a valid table QR code.';
      this.submitError.set(errorMsg);
      throw new Error(errorMsg);
    }

    // Gated Payment Requirement
    if (this.isPaymentRequired()) {
      this.selectedPaymentType.set(1);
    }

    // Location Check: prompt/verify diner location if enabled
    if (this.settings.locationCheckSettings().Enabled) {
      try {
        await this.geoService.verifyLocation();
      } catch (geoErr: any) {
        const errorMsg = geoErr?.message || this.translate.instant('LOCATION.PERMISSION_REQUIRED');
        this.submitError.set(errorMsg);
        throw geoErr;
      }
    }

    const effectiveTableID = type === OnlineOrderType.DINE_IN ? (tableID || null) : null;
    const currency = this.settings.selectedCurrency();
    const patronId = this.getOrCreatePatronId();

    // Prepare clean OrderItems for POST /v1/online-order/qr-menu (Go models expect Name and OrderItemFeature as JSON strings)
    const orderItemsPayload: any[] = this.orderItems().map(item => {
      const calculated = checkSetOrderItem({ ...item });
      // Item description is specific to this item only; never override with order/session notes
      const itemDesc = (calculated.Description && calculated.Description.trim().length > 0)
        ? calculated.Description.trim()
        : '';

      const features = (calculated.OrderItemFeature || []).map(f => ({
        id: f.id,
        OptionID: f.OptionID || f.id,
        ProductID: f.ProductID || calculated.ProductID,
        ReferenceProductID: f.ReferenceProductID || '',
        Name: f.Name,
        Type: typeof f.Type === 'number' ? f.Type : 0,
        Modifier: typeof f.Modifier === 'number' ? f.Modifier : 0,
        Quantity: typeof f.Quantity === 'number' && f.Quantity > 0 ? f.Quantity : 1,
        OptionGroupID: f.OptionGroupID || '',
        SID: f.SID || '',
        MinQuantity: f.MinQuantity ?? 0,
        MaxQuantity: f.MaxQuantity ?? 1,
      }));

      return {
        VenueID: customer.id,
        CustomerID: customer.id,
        ProductID: calculated.ProductID,
        Name: typeof calculated.Name === 'string' ? calculated.Name : JSON.stringify(calculated.Name),
        SID: calculated.SID || '',
        Quantity: calculated.Quantity,
        ProductType: calculated.ProductType || 0,
        OrderType: calculated.OrderType || 0,
        OrderItemFeature: JSON.stringify(features),
        Description: itemDesc,
        UnitBasePrice: calculated.UnitBasePrice,
        UnitFeaturePrice: calculated.UnitFeaturePrice || 0,
        UnitDiscountPrice: calculated.UnitDiscountPrice || 0,
        UnitTaxablePrice: calculated.UnitTaxablePrice || 0,
        OrderItemGrossAmount: calculated.OrderItemGrossAmount,
        OrderItemDiscountAmount: calculated.OrderItemDiscountAmount || 0,
        OrderItemTaxableAmount: calculated.OrderItemTaxableAmount || 0,
        OrderItemTaxPercentage: calculated.OrderItemTaxPercentage || 0,
        OrderItemTaxAmount: calculated.OrderItemTaxAmount || 0,
        OrderItemTotalPayable: calculated.OrderItemTotalPayable,
        DefaultCurrencyID: currency?.id || '',
        TableID: effectiveTableID || '',
        FloorID: type === OnlineOrderType.DINE_IN ? (this.settings.floorID() || '') : '',
      };
    });

    const totalGross = this.grossTotal();
    const totalDiscount = Number(this.orderItems().reduce((sum, item) => sum + (item.OrderItemDiscountAmount || 0), 0).toFixed(2));
    const totalTaxable = Number(this.orderItems().reduce((sum, item) => sum + (item.OrderItemTaxableAmount || 0), 0).toFixed(2));
    const totalTax = this.taxTotal();
    const totalPayable = this.payableTotal();

    // Branch for Stripe Online Payment
    if (this.selectedPaymentType() === 1) {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const tableQuery = effectiveTableID ? `&tableID=${encodeURIComponent(effectiveTableID)}` : '';
      const successUrl = `${origin}/${customer.id}/order-success?session_id={CHECKOUT_SESSION_ID}${tableQuery}`;

      let cancelUrl = origin;
      if (typeof window !== 'undefined' && typeof window.location !== 'undefined') {
        try {
          const cancelUrlObj = new URL(window.location.href);
          cancelUrlObj.searchParams.set('payment_cancelled', 'true');
          cancelUrl = cancelUrlObj.toString();
        } catch (_) {
          cancelUrl = window.location.href;
        }
      }

      this.isSubmitting.set(true);
      this.submitError.set(null);

      try {
        const stripeRes = await this.pb.createStripeCheckoutSession({
          VenueID: customer.id,
          CustomerID: customer.id,
          DefaultCurrencyID: currency?.id || '',
          Type: type,
          TableID: effectiveTableID,
          PatronID: patronId,
          Description: this.orderNotes().trim(),
          OrderItems: orderItemsPayload,
          TipAmount: this.tipAmount(),
          DeliveryFee: this.deliveryFee(),
          CustomerEmail: this.customerEmail().trim() || undefined,
          SuccessURL: successUrl,
          CancelURL: cancelUrl,
        });

        try {
          localStorage.setItem('BISTRONEX_PENDING_STRIPE_ORDER', JSON.stringify({
            onlineOrderId: stripeRes.online_order_id,
            sessionId: stripeRes.session_id,
            customerId: customer.id,
            tableId: effectiveTableID,
            timestamp: Date.now(),
          }));
        } catch (_) { }

        if (typeof window !== 'undefined') {
          window.location.href = stripeRes.checkout_url;
        }

        return {
          id: stripeRes.online_order_id,
          VenueID: customer.id,
          CustomerID: customer.id,
          PayableAmount: this.finalPayableTotal(),
        } as any;
      } catch (err: any) {
        console.error('Stripe checkout error:', err);
        const message = err?.message || this.translate.instant('ERRORS.ORDER_SUBMIT_FAILED') || 'An error occurred while creating payment session.';
        this.submitError.set(message);
        throw err;
      } finally {
        this.isSubmitting.set(false);
      }
    }

    const orderPayload: any = {
      VenueID: customer.id,
      CustomerID: customer.id,
      PatronID: patronId,
      DefaultCurrencyID: currency?.id || '',
      Type: type,
      Source: OnlineOrderSource.QR_MENU,
      Status: OnlineOrderStatus.PENDING,
      TableID: effectiveTableID,
      Description: this.orderNotes().trim(),
      OrderItems: orderItemsPayload,
      GrossAmount: totalGross,
      DiscountAmount: totalDiscount,
      TaxAmount: totalTax,
      TaxableAmount: totalTaxable,
      DeliveryFee: this.deliveryFee(),
      TipAmount: this.tipAmount(),
      PayableAmount: this.finalPayableTotal(),
      PaymentType: this.selectedPaymentType(),
      PaymentStatus: 0,
    };

    this.isSubmitting.set(true);
    this.submitError.set(null);

    try {
      const createdOrder = await this.pb.createOnlineOrder(orderPayload);
      this.registerActiveOrder(createdOrder, patronId);
      this.clearCart();
      this.isCartOpen.set(false);
      this.isTrackingOpen.set(true);

      return createdOrder;
    } catch (err: any) {
      console.error('Order submission error:', err);
      const message = err?.message || this.translate.instant('ERRORS.ORDER_SUBMIT_FAILED') || 'An error occurred while submitting your order.';
      this.submitError.set(message);
      throw err;
    } finally {
      this.isSubmitting.set(false);
    }
  }

  public registerActiveOrder(createdOrder: OnlineOrder, patronId?: string) {
    this.activeOrders.update(list => {
      const next = [...list.filter(o => o.id !== createdOrder.id), createdOrder];
      next.sort((a, b) => parseUtcEpochMs(a.created) - parseUtcEpochMs(b.created));
      return next;
    });
    if (createdOrder.id) {
      this.selectedTrackingOrderId.set(createdOrder.id);
      const storedIds: string[] = [];
      try {
        const saved = localStorage.getItem(MY_ORDERS_STORAGE_KEY);
        if (saved) storedIds.push(...JSON.parse(saved));
      } catch (_) { }
      if (!storedIds.includes(createdOrder.id)) {
        storedIds.push(createdOrder.id);
        localStorage.setItem(MY_ORDERS_STORAGE_KEY, JSON.stringify(storedIds));
      }
      // Immediately subscribe to this newly created order!
      this.subscribeToOrder(createdOrder.id);
      if (patronId) {
        this.subscribeToPatron(patronId);
      }
      this.startBackupPolling();
    }
  }

  // ==========================================
  // Realtime Order Updates (WebSocket / SSE)
  // Per-order record subscription (OnlineOrder/:id)
  // + Active Backup Polling for 100% resilience
  // ==========================================

  private subscribedOrderIds = new Set<string>();
  private isPatronSubscribed = false;
  private backupPollTimer: any = null;

  /**
   * Subscribes to a specific order by its PocketBase record ID (topic: OnlineOrder/:id).
   * Headers and credentials are automatically applied by PBService.
   * Runs callbacks inside Angular NgZone for immediate UI updates.
   */
  public async subscribeToOrder(orderId: string) {
    if (!orderId || this.subscribedOrderIds.has(orderId)) {
      return;
    }

    this.subscribedOrderIds.add(orderId);

    try {
      await this.pb.subscribeOnlineOrders(orderId, (e) => {
        this.ngZone.run(() => {
          if (e.action === 'update' || e.action === 'create') {
            const updated = e.record;
            this.handleOrderUpdated(updated);
          } else if (e.action === 'delete') {
            this.activeOrders.update(list => list.filter(o => o.id !== orderId));
            this.unsubscribeFromOrder(orderId);
          }
        });
      });
    } catch (err) {
      console.warn(`Failed to subscribe to order ${orderId}:`, err);
      this.subscribedOrderIds.delete(orderId);
    }
  }

  /**
   * Subscribes to patron-scoped order events (topic: OnlineOrder/* with PatronID filter)
   * to catch orders created in another tab/window for this same browser patron.
   */
  public async subscribeToPatron(patronId: string) {
    if (!patronId || this.isPatronSubscribed) {
      return;
    }

    this.isPatronSubscribed = true;

    try {
      await this.pb.subscribeOnlineOrders(
        '*',
        (e) => {
          this.ngZone.run(() => {
            if (e.action === 'create' || e.action === 'update') {
              const ord = e.record;
              if (ord && ord.PatronID === patronId && ord.id) {
                this.handleOrderUpdated(ord);
                this.subscribeToOrder(ord.id);
              }
            }
          });
        },
        { filter: `PatronID = "${patronId}"` }
      );
    } catch (err) {
      console.warn('Patron realtime subscription failed:', err);
      this.isPatronSubscribed = false;
    }
  }

  private handleOrderUpdated(updatedRecord: OnlineOrder) {
    if (!updatedRecord || !updatedRecord.id) return;

    this.activeOrders.update(list => {
      const index = list.findIndex(o => o.id === updatedRecord.id);
      if (index >= 0) {
        const copy = [...list];
        copy[index] = updatedRecord;
        return copy;
      } else if (updatedRecord.Status !== OnlineOrderStatus.CANCELLED) {
        const next = [...list, updatedRecord];
        next.sort((a, b) => parseUtcEpochMs(a.created) - parseUtcEpochMs(b.created));
        return next;
      }
      return list;
    });

    this.updateStoredOrderIds(this.activeOrders());

    // If all orders are delivered/cancelled, stop backup polling
    const hasPendingOrders = this.activeOrders().some(
      o => o.Status < OnlineOrderStatus.DELIVERED && o.Status !== OnlineOrderStatus.CANCELLED
    );
    if (!hasPendingOrders) {
      this.stopBackupPolling();
    }
  }

  public unsubscribeFromOrder(orderId: string) {
    if (!orderId) return;
    this.subscribedOrderIds.delete(orderId);
    try {
      this.pb.unsubscribeOnlineOrders(orderId);
    } catch (_) { }
  }

  public stopAllSubscriptions() {
    this.stopBackupPolling();

    for (const id of this.subscribedOrderIds) {
      try {
        this.pb.unsubscribeOnlineOrders(id);
      } catch (_) { }
    }
    this.subscribedOrderIds.clear();

    if (this.isPatronSubscribed) {
      this.isPatronSubscribed = false;
      try {
        this.pb.unsubscribeOnlineOrders('*');
      } catch (_) { }
    }
  }

  /**
   * Lightweight backup poller running while non-terminal orders exist.
   * Guarantees that even if mobile browser pauses SSE or server drops an event,
   * the UI is refreshed within 2.5 seconds.
   */
  public startBackupPolling() {
    if (this.backupPollTimer) return;
    this.backupPollTimer = setInterval(() => {
      this.refreshActiveOrders();
    }, 10000);
  }

  public stopBackupPolling() {
    if (this.backupPollTimer) {
      clearInterval(this.backupPollTimer);
      this.backupPollTimer = null;
    }
  }

  public refreshActiveOrders() {
    const active = this.activeOrders().filter(
      o => o.Status < OnlineOrderStatus.DELIVERED && o.Status !== OnlineOrderStatus.CANCELLED
    );
    if (active.length === 0) {
      this.stopBackupPolling();
      return;
    }
    for (const ord of active) {
      if (ord.id) {
        this.pb.getOnlineOrder(ord.id).then(rec => {
          if (rec) {
            this.ngZone.run(() => {
              this.handleOrderUpdated(rec);
            });
          }
        }).catch(() => { });
      }
    }
  }

  public startPollingActiveOrders() {
    for (const ord of this.activeOrders()) {
      if (ord.id) this.subscribeToOrder(ord.id);
    }
    const patronId = this.getExistingPatronId();
    if (patronId) this.subscribeToPatron(patronId);
    this.startBackupPolling();
  }

  public stopPolling() {
    this.stopAllSubscriptions();
  }

  public startRealtimeSubscription() {
    this.startPollingActiveOrders();
  }

  public stopRealtimeSubscription() {
    this.stopAllSubscriptions();
  }

  private updateStoredOrderIds(orders: OnlineOrder[]) {
    const activeIds = orders
      .filter(o => o.Status !== OnlineOrderStatus.DELIVERED && o.Status !== OnlineOrderStatus.CANCELLED)
      .map(o => o.id!)
      .filter(Boolean);
    try {
      localStorage.setItem(MY_ORDERS_STORAGE_KEY, JSON.stringify(activeIds));
    } catch (_) { }
  }

  public subscribeToOrderUpdates(orderId: string) {
    if (!this.activeOrders().some(o => o.id === orderId)) {
      this.pb.getOnlineOrder(orderId).then(rec => {
        if (rec) {
          this.ngZone.run(() => {
            this.activeOrders.update(list => [rec, ...list.filter(o => o.id !== rec.id)]);
          });
        }
      }).catch(() => { });
    }
    this.subscribeToOrder(orderId);
    this.startBackupPolling();
  }
}


